(ns manatee.admin.handlers
  (:require
   [ajax.core :refer [json-response-format]]
   [ajax.edn :refer [edn-request-format edn-response-format]]
   [goog.string.format]
   [kee-frame.core :as kf]
   [manatee.admin.app-db :as admin-db]
   [manatee.admin.pending-items.handlers :as pending-handlers]
   [manatee.admin.queue.handlers :as admin-queue-handlers]
   [manatee.admin.rejection-option.handlers :as admin-rejection-handlers]
   [manatee.admin.task-config.app-db :as task-db]
   [manatee.ajax :as ajax]
   [manatee.annotation.handlers :as annotation-handlers]
   [manatee.components.notifications.handlers :as notifications]
   [manatee.user.handlers :as user-handlers]
   [re-frame.core :as rf]
   [taoensso.timbre :as log]))

(kf/reg-chain
 ::load-users
 (fn load-users
   [_ _]
   {:http-xhrio {:method :get
                 :uri (kf/path-for [:api/users])
                 :on-failure [::load-users-failure]
                 :response-format (ajax/edn-response-format)}})
 (fn load-users-success
   [{:keys [db]} [users]]
   (let [users (map user-handlers/user<-api users)]
     {:db (admin-db/set-users db users)})))

(rf/reg-event-fx
 ::load-users-failure
 (fn load-users-failure
   [_ [_ info]]
   (log/error "Error loading users" {:info info})
   {:dispatch [::notifications/add-alert :main
               {:message "Unable to load users. Refresh the browser to try again or contact support if necessary."
                :type :error}
               :auto-dismiss? false]}))

(rf/reg-event-fx
 ::load-users-if-not-loaded
 (fn load-users-if-not-loaded
   [{:keys [db]} _]
   (when-not (seq (admin-db/get-users db))
     {:dispatch-n [[::load-users]
                   [::load-user-teams]]})))

(kf/reg-chain
 ::load-user-teams
 (fn load-user-teams
   [_ _]
   {:http-xhrio {:method :get
                 :uri (kf/path-for [:api/user-teams])
                 :on-failure [::load-user-teams-failure]
                 :response-format (ajax/edn-response-format)}})
 (fn load-user-teams-success
   [{:keys [db]} [user-teams]]
   {:db (admin-db/set-user-teams db (user-handlers/teams<-api user-teams))}))

(rf/reg-event-fx
 ::load-user-teams-failure
 (fn load-user-teams-failure
   [{:keys [db]} [_ info]]
   (log/error "Error loading user teams" {:info info})
   {:db (admin-db/set-user-teams db [{:id nil :name "Unable to load teams: Refresh browser or contact support"}])}))

(rf/reg-event-db
 ::set-team-filter
 (fn [db [_ filter]]
   (admin-db/set-team-filter db filter)))

(rf/reg-event-db
 ::select-user
 (fn select-user
   [db [_ user-id]]
   (admin-db/set-selected-user db user-id)))

(rf/reg-event-db
 ::select-task
 (fn select-user
   [db [_ task-id]]
   (task-db/set-selected-task db task-id)))

(rf/reg-event-db
 ::clear-selected-task
 (fn clear-selected-task
   [db]
   (task-db/clear-selected-task db)))

(rf/reg-event-fx
 ::select-tab
 (fn select-tab
   [{:keys [db]} [_ tab]]
   (let [tab (get #{:users :queue :tasks :rejections :messages :pending :user-guide} tab :users)]
     {:db (admin-db/set-selected-tab db tab)})))

(rf/reg-event-fx
 ::load-admin-view
 [(rf/inject-cofx :current-url)]
 (fn load-admin-view
   [{:keys [db]} params]
   (let [[_ initial-tab] params]
     {:db db
      :dispatch-n [[::select-tab initial-tab]
                   [::admin-queue-handlers/load-queue-configs]
                   [::admin-rejection-handlers/load-rejection-options]
                   [::load-users]
                   [::load-user-teams]
                   [::fetch-messages]
                   ; TODO: Should the events dispatched above be conditional on the tab rather than fire for every one?
                   ; One would need to look at which tabs want which info.
                   (when (= initial-tab :pending) [::pending-handlers/fetch-pending-items nil])]})))

(kf/reg-controller ::admin
                   {:params (fn [{:keys [handler] :as params}]
                              (when (= handler ::admin)
                                params))
                    :start (fn [_ params]
                             (let [tab (or (-> (get-in params [:query :tab]) keyword)
                                           :users)]
                               (when params
                                 [::load-admin-view tab])))})

(rf/reg-event-fx
 ::fetch-messages
 (fn [_ _]
   {:http-xhrio {:method          :get
                 :uri             (kf/path-for [:api/messages])
                 :response-format (ajax/edn-response-format)
                 :on-success      [::fetch-messages-success]
                 :on-failure      [::fetch-messages-failure]}
    :dispatch   [::annotation-handlers/set-loading? true]}))

(rf/reg-event-fx
 ::fetch-messages-success
 (fn [{:keys [db]} [_ msg]]
   {:db       (admin-db/set-messages db msg)
    :dispatch [::annotation-handlers/set-loading? false]}))

(rf/reg-event-fx
 ::fetch-messages-failure
 (fn [_ [_ info]]
   (log/error "Error loading messages" (str info))
   {:fx [[:dispatch [::notifications/add-alert ::fetch-messages-failure
                     {:message "Unable to load messages. Try again."
                      :type    :error}
                     :auto-dismiss? false]]
         [:dispatch [::annotation-handlers/set-loading? false]]]}))

(rf/reg-event-db
 ::select-message
 (fn select-message
   [db [_ message-id]]
   (admin-db/set-selected-message db message-id)))

(rf/reg-event-fx
 ::save-message
 (fn [_ [_ id {:keys [start-time end-time] :as message}]]
   (let [start (admin-db/parse-editable-time start-time)
         end (admin-db/parse-editable-time end-time)]
     (cond (not start)
           {:dispatch [::notifications/add-alert ::save-message
                       {:message "Unable to save message. Invalid start time."
                        :type    :error}
                       :auto-dismiss? false]}

           (not end)
           {:dispatch [::notifications/add-alert ::save-message
                       {:message "Unable to save message. Invalid end time."
                        :type    :error}
                       :auto-dismiss? false]}

           :else
           (let [message (assoc message
                                :start-time (admin-db/format-api-timestamp start)
                                :end-time (admin-db/format-api-timestamp end))]
             {:http-xhrio {:method          :post
                           :uri             (kf/path-for [:api/messages])
                           :params          message
                           :format          (edn-request-format)
                           :response-format (json-response-format {:keywords? true})
                           :on-success      [::save-message-success id]
                           :on-failure      [::save-message-failure]}})))))

(rf/reg-event-fx
 ::save-message-success
 (fn [{:keys [db]} [_ old-id]]
   (if old-id
     {:fx [[:dispatch [::delete-message old-id "Saved, but unable to delete old version of message. Try deleting manually."]]]}
     {:db (admin-db/set-messages db [])
      :fx [[:dispatch [::fetch-messages]]]})))

(rf/reg-event-fx
 ::save-message-failure
 (fn [_ [_ info]]
   (log/error "Error saving message" (str info))
   {:fx [[:dispatch [::notifications/add-alert ::save-message-failure
                     {:message "Unable to save message. Try again."
                      :type    :error}
                     :auto-dismiss? false]]
         [:dispatch [::fetch-messages]]]}))

(rf/reg-event-db
 ::toggle-message-mark
 (fn [db [_ id]]
   (admin-db/toggle-message-mark db id)))

(rf/reg-event-fx
 ::purge-message
 (fn [{:keys [db]} _]
   (let [{:keys [id]}  (admin-db/get-marked-message db)]
     {:dispatch [::delete-message id "The message could not be purged. Try again or contact developers."]})))

(rf/reg-event-fx
 ::delete-message
 (fn [_ [_ id failure-text]]
   {:http-xhrio {:method          :delete
                 :uri             (kf/path-for [:api/messages])
                 :params          {:id id}
                 :format          (edn-request-format)
                 :response-format (ajax/edn-response-format)
                 :on-success      [::fetch-messages]
                 :on-failure      [::purge-message-failure failure-text]}}))

(rf/reg-event-fx
 ::purge-message-failure
 (fn [_ [_ failure-text info]]
   (log/error "Error deleting message" (str info))
   {:fx [[:dispatch [::notifications/add-alert ::purge-message-failure
                     {:message failure-text
                      :type    :error}
                     :auto-dismiss? false]]
         [:dispatch [::fetch-messages]]]}))

(kf/reg-event-fx
 ::delete-task
 [(rf/inject-cofx :cookie/get [:auth-token])]
 (fn delete-task
   [{:keys [db]} [task-id]]
   {:db db
    :http-xhrio {:method :delete
                 :uri (kf/path-for [:api/task-configs])
                 :params {:task-config-id task-id}
                 :format (edn-request-format)
                 :response-format (ajax/edn-response-format)
                 :on-success [::delete-task-success task-id]
                 :on-failure [::delete-task-failure]}}))

(rf/reg-event-fx
 ::delete-task-success
 (fn delete-task-success
   [{:keys [db]} [_ _ _response]]
   {:db db
    :dispatch-n [[::clear-selected-task]
                 [::annotation-handlers/load-task-configs]
                 [::notifications/add-alert :main
                  {:message "Task deleted successfully"
                   :type :info}
                  :auto-dismiss? true]]}))

(rf/reg-event-fx
 ::delete-task-failure
 (fn delete-task-failure
   [{:keys [db]} [_ {:keys [response]}]]
   {:db db
    :dispatch-n [[::notifications/add-alert :main
                  {:message (or (get-in response [:reason :message])
                                "Unable to delete task. There may already be edits for this task! Contact support if necessary.")
                   :type :error}
                  :auto-dismiss? true]]}))

(rf/reg-event-db
 ::set-user-guide-payload
 (fn set-user-guide-payload
   [db [_ file]]
   (admin-db/set-user-guide-payload db file)))

(rf/reg-event-fx
 ::upload-user-guide
 [(rf/inject-cofx :cookie/get [:auth-token])]
 (fn upload-user-guide
   [{:keys [db]} _]
   (let [file (admin-db/get-user-guide-payload db)]
     {:http-xhrio {:method :put
                   :uri (kf/path-for [:api/user-guide])
                   :body (doto (js/FormData.)
                           (.set "file" file "Manatee_User_Guide"))
                   :response-format (edn-response-format)
                   :on-success [::upload-user-guide-success]
                   :on-failure [::upload-user-guide-failure]}})))

(rf/reg-event-fx
 ::upload-user-guide-success
 (fn [_ _]
   {:dispatch [::notifications/add-alert :main
               {:message "User Guide uploaded"
                :type :info}
               :auto-dismiss? true]}))

(rf/reg-event-fx
 ::upload-user-guide-failure
 (fn [_ _]
   {:dispatch [::notifications/add-alert :main
               {:message "Unable to upload User Guide"
                :type :error}
               :auto-dismiss? false]}))
